import React from 'react';
import { useTranslation } from 'react-i18next';
import AccountShell from './AccountShell';
import { trackMonetizationEvent } from '../../services/telemetry';
import { trackCheckoutSuccess } from '../../services/analytics';
import { buildUpgradeQuery, parseAttribution, parseUpgradeParams } from './upgradeOffers';
import './UpgradeStatusPage.css';

interface Props {
  kind: 'success' | 'cancel';
}

// ── Icons ─────────────────────────────────────────────────────────────────

const CheckIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M8 16.5l5.5 5.5L24 11" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SmallCheckIcon: React.FC = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
    <path d="M2 5.5l2.5 2.5L9 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M8 8l12 12M20 8L8 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const ArrowRightIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Pro features unlocked list ────────────────────────────────────────────

// prayerTimes intentionally absent: free for everyone since 2026-08-03
// (see src/hooks/useFeatureAccess.ts) — it is not something Pro unlocks.
const UNLOCKED_FEATURE_KEYS = [
  'weatherForecast',
  'verseFavorites',
  'notesAi',
  'priorityTasks',
  'verseReflection',
];

/** Canonical CWS listing (item id hfahicbimhmjceekiagpjagoljaldeki). */
const CHROME_WEB_STORE_URL = 'https://chromewebstore.google.com/detail/hfahicbimhmjceekiagpjagoljaldeki';

/** A cold email buyer may not have the extension yet; it only installs on desktop Chromium. */
const canInstallChromeExtension = (): boolean =>
  typeof navigator !== 'undefined'
  && /Chrome\//.test(navigator.userAgent)
  && !/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

// ── Component ─────────────────────────────────────────────────────────────

const UpgradeStatusPage: React.FC<Props> = ({ kind }) => {
  const { t } = useTranslation(['account']);
  const isSuccess = kind === 'success';
  // The Stripe cancel URL carries plan/promo/utm; re-validate them and send them
  // back to /upgrade so backing out of Stripe doesn't lose the offer.
  const [backToUpgradeHref] = React.useState(() => {
    const search = window.location.search;
    return `/upgrade${buildUpgradeQuery({ ...parseUpgradeParams(search), ...parseAttribution(search) })}`;
  });
  const [showAddToChrome] = React.useState(canInstallChromeExtension);

  React.useEffect(() => {
    if (isSuccess) {
      void trackMonetizationEvent('checkout_success', 'upgrade_status_page');
      // telemetry.ts is a no-op unless a self-hosted collector is configured;
      // this is the first-party counter that actually reaches AnalyticsDaily.
      trackCheckoutSuccess();
    }
  }, [isSuccess]);

  if (isSuccess) {
    return (
      <AccountShell section="upgrade">
        <div className="usp-root">

          <div className="usp-icon-wrap usp-icon-wrap--success" aria-hidden="true">
            <CheckIcon />
          </div>

          <div className="usp-text">
            <h1 className="usp-title usp-title--success">{t('account:upgradeStatus.success.title')}</h1>
            <p className="usp-desc">
              {t('account:upgradeStatus.success.desc')}
            </p>
            {showAddToChrome && (
              <a className="usp-inline-link" href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
                {t('account:upgradeStatus.success.addToChrome')}
              </a>
            )}
          </div>

          <div className="usp-details" aria-label={t('account:upgradeStatus.unlockedFeatures_aria')}>
            {UNLOCKED_FEATURE_KEYS.map((k) => (
              <div key={k} className="usp-detail-item">
                <span className="usp-detail-icon">
                  <SmallCheckIcon />
                </span>
                {t(`account:upgradeStatus.unlockedFeatures.${k}`)}
              </div>
            ))}
          </div>

          <div className="usp-actions">
            <a className="usp-primary-btn" href="/billing">
              {t('account:upgradeStatus.success.viewBilling')}
              <ArrowRightIcon />
            </a>
            <button
              type="button"
              className="usp-ghost-link"
              onClick={() => window.close()}
            >
              {t('account:upgradeStatus.success.closeTab')}
            </button>
          </div>

        </div>
      </AccountShell>
    );
  }

  return (
    <AccountShell section="upgrade">
      <div className="usp-root">

        <div className="usp-icon-wrap usp-icon-wrap--cancel" aria-hidden="true">
          <XIcon />
        </div>

        <div className="usp-text">
          <h1 className="usp-title">{t('account:upgradeStatus.cancel.title')}</h1>
          <p className="usp-desc">
            {t('account:upgradeStatus.cancel.desc')}
          </p>
        </div>

        <div className="usp-actions">
          <a className="usp-secondary-btn" href={backToUpgradeHref}>
            {t('account:upgradeStatus.cancel.backToUpgrade')}
          </a>
          <a className="usp-ghost-link" href="/">
            {t('account:upgradeStatus.cancel.returnToExtension')}
          </a>
        </div>

      </div>
    </AccountShell>
  );
};

export default UpgradeStatusPage;
